<template>
    <div>
        <div class="tw:space-y-4">
            <div class="tw:mb-4 tw:px-6">
                <h2 class="tw:text-sm tw:font-semibold tw:text-gray-500 tw:uppercase tw:tracking-wide tw:my-1">Plazos pendientes</h2>
                <p class="tw:text-xs tw:font-semibold tw:text-gray-400 tw:my-1">Calendario de pagos pendientes</p>
            </div>

            <div class="tw:flex tw:justify-end tw:px-6">
                <Button
                    class="tw:text-xs tw:sm:text-sm"
                    :disabled="!hasPendingPlazos"
                    @click="openPagarTodosModal"
                >
                    Pagar todos los plazos
                </Button>
            </div>

            <SimpleTable
                :flat="true"
                :columns="columns"
                :data="plazos"
                empty-text="No hay plazos pendientes"
                :default-col-width="defaultColWidth"
            >
                <template #cell-fecha="{ value }">
                    {{ formatDate(value) }}
                </template>

                <template #cell-metodo_pago="{ value }">
                    {{ value || '-' }}
                </template>

                <template #cell-estado_string="{ row }">
                    <span :class="['tw:px-2 tw:py-1 tw:rounded tw:text-xs tw:font-medium', getStatusClass(row.estado_string)]">
                        {{ getStatusLabel(row.estado_string) }}
                    </span>
                </template>

                <template #cell-informacion="{ value }">
                    {{ value || '-' }}
                </template>

                <template #cell-descuento_aplicado="{ value }">
                    {{ value | formatear_precio }}
                </template>

                <template #cell-importe="{ value }">
                    <span class="tw:text-left tw:block">{{ value | formatear_precio }}</span>
                </template>

                <template #cell-opciones="{ row }">
                    <OptionsDropdownComponent
                        v-if="!isPlazoCompleto(row)"
                        :options="getRowOptions(row)"
                    />
                </template>
            </SimpleTable>

            <div v-if="plazos.length > 0" class="tw:p-4 tw:space-y-2 tw:px-6">
                <div class="tw:flex tw:justify-between tw:items-center">
                    <span class="tw:font-bold tw:text-gray-700">TOTAL:</span>
                    <span class="tw:text-lg tw:font-bold tw:text-gray-900">{{ totalAmount | formatear_precio }}</span>
                </div>
                <div class="tw:flex tw:justify-between tw:items-center">
                    <span class="tw:font-bold tw:text-gray-700">Importe restante:</span>
                    <span class="tw:text-lg tw:font-bold tw:text-blue-600">{{ remainingAmount | formatear_precio }}</span>
                </div>
            </div>
        </div>

        <!-- Modales de plazo (serán creados en Phase 4) -->
        <PagarTodosPlazosModal
            :importe="pendingPlazosAmount"
            :plazos-count="pendingPlazosCount"
            @pagar-todos="handlePagarTodos"
            @close="closePagarTodosModal"
        />

        <PagarPlazoModal
            v-if="selectedPlazoForPayment"
            :importe="selectedPlazoForPayment.importe"
            :plazo-id="selectedPlazoForPayment.id"
            @pagar-plazo="handlePagarPlazo"
            @close="closePagarPlazoModal"
        />

        <DescuentoPlazoModal
            v-if="selectedPlazoForDiscount"
            :importe="selectedPlazoForDiscount.importe"
            :plazo-id="selectedPlazoForDiscount.id"
            @descontar-plazo="handleDescontarPlazo"
            @close="closeDescuentoPlazoModal"
        />
    </div>
</template>

<script setup>
    import { ref, computed, getCurrentInstance, nextTick } from 'vue'
    import SimpleTable from '@/modules/admin/ui/table/SimpleTable.vue'
    import OptionsDropdownComponent from '@/modules/admin/ui/OptionsDropdownComponent.vue'
    import Button from '@/modules/admin/ui/Button.vue'
    // Modales forkeados de abonado — serán creados en Phase 4
    import PagarPlazoModal from '@/modules/admin/bases/components/modals/PagarPlazoModal.vue'
    import PagarTodosPlazosModal from '@/modules/admin/bases/components/modals/PagarTodosPlazosModal.vue'
    import DescuentoPlazoModal from '@/modules/admin/bases/components/modals/DescuentoPlazoModal.vue'

    const instance = getCurrentInstance()

    const openPagarTodosModal = () => {
        instance.proxy.$modal.show('pagar-todos-plazos-modal')
    }

    const openPagarPlazoModal = plazoId => {
        instance.proxy.$modal.show('pagar-plazo-' + plazoId)
    }

    const openDescuentoModal = plazoId => {
        instance.proxy.$modal.show('descuento-plazo-' + plazoId)
    }

    const props = defineProps({
        plazos: {
            type: Array,
            default: () => []
        },
        base: {
            type: Object,
            default: () => ({})
        },
        defaultColWidth: {
            type: String,
            default: '140px'
        }
    })

    const emit = defineEmits(['pagar-plazo', 'pagar-plazos', 'descontar-plazo', 'generar-enlace-pago'])

    const columns = computed(() => [
        { key: 'fecha', label: 'Fecha cobro' },
        { key: 'metodo_pago', label: 'Método de pago' },
        { key: 'estado_string', label: 'Estado' },
        { key: 'informacion', label: 'Motivo del fallo' },
        { key: 'descuento_aplicado', label: 'Descuento' },
        { key: 'importe', label: 'Total' },
        { key: 'opciones', label: '' }
    ])

    const formatDate = dateString => {
        if (!dateString) return '-'
        try {
            const date = new Date(dateString)
            return date.toLocaleDateString('es-AR', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit'
            })
        } catch {
            return '-'
        }
    }

    const getStatusClass = estadoString => {
        switch (estadoString?.toUpperCase()) {
            case 'PAGADO':
                return 'tw:bg-green-100 tw:text-green-800'
            case 'PARCIAL':
                return 'tw:bg-yellow-100 tw:text-yellow-800'
            default:
                return 'tw:bg-gray-100 tw:text-gray-800'
        }
    }

    const getStatusLabel = estadoString => {
        return estadoString || 'Pendiente'
    }

    const selectedPlazoForPayment = ref(null)
    const selectedPlazoForDiscount = ref(null)

    const isPlazoCompleto = plazo => {
        return plazo.estado_string?.toUpperCase() === 'PAGADO'
    }

    const hasPendingPlazos = computed(() => {
        return props.plazos && props.plazos.some(p => !isPlazoCompleto(p))
    })

    const pendingPlazos = computed(() => {
        return (props.plazos || []).filter(p => !isPlazoCompleto(p))
    })

    const pendingPlazosCount = computed(() => pendingPlazos.value.length)

    const pendingPlazosAmount = computed(() => {
        return pendingPlazos.value.reduce((sum, p) => sum + (p.importe || 0), 0)
    })

    const totalAmount = computed(() => {
        return (props.plazos || []).reduce((sum, p) => sum + (p.importe || 0), 0)
    })

    const remainingAmount = computed(() => {
        return (props.plazos || [])
            .filter(p => p.estado_string?.toUpperCase() !== 'PAGADO')
            .reduce((sum, p) => sum + (p.importe || 0), 0)
    })

    const handlePagarTodos = payload => {
        emit('pagar-plazos', payload.metodoId)
    }

    const handlePagarPlazo = payload => {
        emit('pagar-plazo', payload.plazo_id, payload.metodoId)
    }

    const handleDescontarPlazo = payload => {
        emit('descontar-plazo', payload.descuento_integro, payload.descuento_porcentaje, payload.plazo_id)
    }

    const closePagarPlazoModal = () => {
        const id = selectedPlazoForPayment.value?.id
        selectedPlazoForPayment.value = null
        if (id) instance.proxy.$modal.hide('pagar-plazo-' + id)
    }

    const closeDescuentoPlazoModal = () => {
        const id = selectedPlazoForDiscount.value?.id
        selectedPlazoForDiscount.value = null
        if (id) instance.proxy.$modal.hide('descuento-plazo-' + id)
    }

    const closePagarTodosModal = () => {
        instance.proxy.$modal.hide('pagar-todos-plazos-modal')
    }

    const getRowOptions = plazo => {
        const options = []

        // Opción "Pagar" — visible si no está pagado
        if (plazo.estado_string !== 'PAGADO') {
            options.push({
                type: 'action',
                action: async() => {
                    selectedPlazoForPayment.value = plazo
                    await nextTick()
                    openPagarPlazoModal(plazo.id)
                },
                label: 'Pagar'
            })
        }

        // Opción "Descontar" — visible si no tiene descuento aplicado
        if (!plazo.descuento_aplicado || plazo.descuento_aplicado === 0) {
            options.push({
                type: 'action',
                action: async() => {
                    selectedPlazoForDiscount.value = plazo
                    await nextTick()
                    openDescuentoModal(plazo.id)
                },
                label: 'Descontar'
            })
        }

        // Opción "Generar enlace de pago" — siempre visible para plazos no pagados
        options.push({
            type: 'action',
            action: () => {
                emit('generar-enlace-pago', plazo.id)
            },
            label: 'Generar enlace de pago'
        })

        return options
    }
</script>
